/**
 * ItemsEditor — T232-PY-FE-1 (sanitized receipt scan manual-edit form)
 *
 * Lightweight inline editor for transaction line items extracted by the
 * receipt scanner when the backend proxy returns 422 + proxy_mapped: true
 * (BE-1 sanitization contract). Each row exposes:
 *   - description (read-only label)
 *   - amount (editable numeric input, h-44)
 *   - quantity (editable numeric input, h-44)
 *   - delete button (44x44 IconButton, destructive)
 *
 * Mobile-first: single-column stack on < sm, two-column amount/quantity on sm+.
 * Empty state guides the user to add a line or re-scan. No new npm deps.
 *
 * @see ReceiptScannerDialog.tsx — parent that mounts this component on 422
 */
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { IconButton } from '@/components/ui/icon-button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { TransactionItem } from '@/lib/types';

export interface EditableReceiptItem extends TransactionItem {
  /** Stable key for React list reconciliation. Falls back to index. */
  key?: string;
}

export interface ItemsEditorProps {
  items: EditableReceiptItem[];
  onChange: (items: EditableReceiptItem[]) => void;
  /** Optional caption shown above the list (e.g. warning summary). */
  caption?: string;
  /** Disables all interactive controls (used during async save). */
  disabled?: boolean;
  className?: string;
}

export default function ItemsEditor({
  items,
  onChange,
  caption,
  disabled = false,
  className,
}: ItemsEditorProps) {
  const updateItem = (index: number, patch: Partial<EditableReceiptItem>) => {
    const next = items.map((it, i) => (i === index ? { ...it, ...patch } : it));
    onChange(next);
  };

  const removeItem = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const clearAll = () => onChange([]);

  return (
    <div
      data-testid="receipt-items-editor"
      className={cn('space-y-3', className)}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium">
          {caption ?? `${items.length} item${items.length === 1 ? '' : 's'}`}
        </p>
        {items.length > 0 && !disabled && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={clearAll}
            className="min-h-[44px] text-xs"
            data-testid="receipt-items-clear-all"
          >
            Clear all
          </Button>
        )}
      </div>

      {items.length === 0 ? (
        <div
          className="rounded-md border border-dashed border-amber-300 bg-amber-50/40 p-4 text-sm text-muted-foreground dark:bg-amber-950/20"
          data-testid="receipt-items-empty"
        >
          No line items remaining. Re-scan with a clearer photo or enter the
          transaction manually.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="receipt-items-list">
          {items.map((item, index) => (
            <li
              key={item.key ?? `item-${index}`}
              data-testid={`receipt-item-row-${index}`}
              className="flex flex-col gap-2 rounded-md border bg-card p-3 sm:flex-row sm:items-center"
            >
              <div className="min-w-0 flex-1">
                <p
                  className="text-sm font-medium break-words [overflow-wrap:anywhere]"
                  data-testid={`receipt-item-desc-${index}`}
                >
                  {item.description || '(no description)'}
                </p>
                {item.emoji ? (
                  <span className="text-xs text-muted-foreground">
                    {item.emoji}
                  </span>
                ) : null}
              </div>

              <div className="grid grid-cols-2 gap-2 sm:flex sm:w-auto sm:items-center">
                <div className="space-y-0.5">
                  <label
                    htmlFor={`receipt-item-amount-${index}`}
                    className="text-[10px] uppercase tracking-wide text-muted-foreground"
                  >
                    Amount
                  </label>
                  <Input
                    id={`receipt-item-amount-${index}`}
                    data-testid={`receipt-item-amount-${index}`}
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step={0.01}
                    disabled={disabled}
                    value={Number.isFinite(item.amount) ? item.amount : 0}
                    onChange={(e) => {
                      const next = parseFloat(e.currentTarget.value);
                      updateItem(index, {
                        amount: Number.isFinite(next) ? next : 0,
                      });
                    }}
                    className="h-[44px] w-full tabular-nums sm:w-24"
                    aria-label={`Amount for ${item.description || `item ${index + 1}`}`}
                  />
                </div>

                <div className="space-y-0.5">
                  <label
                    htmlFor={`receipt-item-qty-${index}`}
                    className="text-[10px] uppercase tracking-wide text-muted-foreground"
                  >
                    Qty
                  </label>
                  <Input
                    id={`receipt-item-qty-${index}`}
                    data-testid={`receipt-item-qty-${index}`}
                    type="number"
                    inputMode="numeric"
                    min={1}
                    step={1}
                    disabled={disabled}
                    value={item.quantity ?? 1}
                    onChange={(e) => {
                      const next = parseInt(e.currentTarget.value, 10);
                      updateItem(index, {
                        quantity: Number.isFinite(next) && next > 0 ? next : 1,
                      });
                    }}
                    className="h-[44px] w-full tabular-nums sm:w-20"
                    aria-label={`Quantity for ${item.description || `item ${index + 1}`}`}
                  />
                </div>

                <IconButton
                  data-testid={`receipt-item-delete-${index}`}
                  variant="ghost"
                  size="sm"
                  tooltip={`Delete ${item.description || 'item'}`}
                  disabled={disabled}
                  onClick={() => removeItem(index)}
                  className="h-[44px] w-[44px] shrink-0 text-destructive hover:bg-destructive/10 hover:text-destructive sm:self-end"
                  aria-label={`Delete ${item.description || `item ${index + 1}`}`}
                >
                  <Trash2 className="h-4 w-4" />
                </IconButton>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}